import { useState, useEffect } from 'react'
import {
  Card, Table, Tag, Button, Statistic, Row, Col, Tabs, Empty, Spin,
  message, Modal, Form, Input, Select, Typography, Progress, Alert
} from 'antd'
import {
  ReloadOutlined, PlusOutlined, SearchOutlined, RiseOutlined,
  FallOutlined, MinusOutlined, TrophyOutlined, LineChartOutlined
} from '@ant-design/icons'
import { Line, Column } from '@ant-design/charts'
import { seoRankingsAPI } from '../services/api'

const { Title, Text } = Typography
const { TabPane } = Tabs

const SEORankings = () => {
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [summary, setSummary] = useState(null)
  const [rankings, setRankings] = useState([])
  const [trends, setTrends] = useState([])
  const [keywords, setKeywords] = useState([])
  const [selectedKeyword, setSelectedKeyword] = useState(null)
  const [historyData, setHistoryData] = useState([])
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('rankings')
  const [form] = Form.useForm()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [summaryData, rankingsData, trendsData, keywordsData] = await Promise.all([
        seoRankingsAPI.getSummary().catch(() => null),
        seoRankingsAPI.getCurrentRankings().catch(() => []),
        seoRankingsAPI.getTrends().catch(() => []),
        seoRankingsAPI.getKeywords().catch(() => [])
      ])

      setSummary(summaryData)
      setRankings(rankingsData || [])
      setTrends(trendsData || [])
      setKeywords(keywordsData || [])
    } catch (err) {
      message.error('Failed to load SEO data')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckNow = async () => {
    setChecking(true)
    try {
      await seoRankingsAPI.triggerCheck()
      message.success('Ranking check started. Refresh in a few minutes.')
    } catch (err) {
      message.error('Failed to start ranking check')
    } finally {
      setChecking(false)
    }
  }

  const fetchKeywordHistory = async (keyword) => {
    setSelectedKeyword(keyword)
    setActiveTab('history')  // Switch to History tab
    try {
      const data = await seoRankingsAPI.getHistory(keyword, 30)
      setHistoryData(data?.data || [])
    } catch (err) {
      message.error('Failed to load history')
    }
  }

  const handleAddKeyword = async (values) => {
    try {
      await seoRankingsAPI.addKeyword(values)
      message.success('Keyword added')
      setAddModalVisible(false)
      form.resetFields()
      fetchData()
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to add keyword')
    }
  }

  const handleDeleteKeyword = async (id) => {
    try {
      await seoRankingsAPI.deleteKeyword(id)
      message.success('Keyword removed')
      fetchData()
    } catch (err) {
      message.error('Failed to remove keyword')
    }
  }

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <RiseOutlined style={{ color: '#52c41a' }} />
      case 'down': return <FallOutlined style={{ color: '#ff4d4f' }} />
      case 'new': return <PlusOutlined style={{ color: '#1890ff' }} />
      case 'lost': return <MinusOutlined style={{ color: '#ff4d4f' }} />
      default: return <MinusOutlined style={{ color: '#999' }} />
    }
  }

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'green'
      case 'down': return 'red'
      case 'new': return 'blue'
      case 'lost': return 'red'
      default: return 'default'
    }
  }

  const rankingsColumns = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 100,
      sorter: (a, b) => (a.position || 999) - (b.position || 999),
      render: (pos) => pos ? (
        <Tag color={pos <= 10 ? 'green' : pos <= 30 ? 'blue' : pos <= 50 ? 'orange' : 'default'}>
          #{pos}
        </Tag>
      ) : <Tag color="red">Not ranked</Tag>
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      ) : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          icon={<LineChartOutlined />}
          onClick={() => fetchKeywordHistory(record.keyword)}
        >
          History
        </Button>
      )
    }
  ]

  const trendsColumns = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Current',
      dataIndex: 'current_position',
      key: 'current',
      width: 100,
      render: (pos) => pos ? `#${pos}` : '-'
    },
    {
      title: 'Previous',
      dataIndex: 'previous_position',
      key: 'previous',
      width: 100,
      render: (pos) => pos ? `#${pos}` : '-'
    },
    {
      title: 'Change',
      dataIndex: 'change',
      key: 'change',
      width: 100,
      render: (change, record) => (
        <span>
          {getTrendIcon(record.trend)}{' '}
          {change !== null ? (change > 0 ? `+${change}` : change) : '-'}
        </span>
      )
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      key: 'trend',
      width: 100,
      render: (trend) => <Tag color={getTrendColor(trend)}>{trend.toUpperCase()}</Tag>
    }
  ]

  const keywordsColumns = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag>{cat}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (active) => <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          danger
          onClick={() => handleDeleteKeyword(record.id)}
        >
          Remove
        </Button>
      )
    }
  ]

  // Chart config for history
  const historyChartConfig = {
    data: historyData.map(d => ({ ...d, position: d.position || 100 })),
    xField: 'date',
    yField: 'position',
    height: 300,
    yAxis: {
      title: { text: 'Position' },
      min: 1,
      max: 100,
      reverse: true  // Lower position = better
    },
    point: { size: 4 },
    tooltip: {
      formatter: (datum) => ({
        name: 'Position',
        value: datum.position === 100 ? 'Not ranked' : `#${datum.position}`
      })
    },
    color: '#1890ff'
  }

  // Chart for category distribution
  const categoryData = keywords.reduce((acc, kw) => {
    const cat = kw.category || 'general'
    const existing = acc.find(d => d.category === cat)
    if (existing) existing.count++
    else acc.push({ category: cat, count: 1 })
    return acc
  }, [])

  const categoryChartConfig = {
    data: categoryData,
    xField: 'category',
    yField: 'count',
    height: 200,
    color: '#1890ff'
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>SEO Rankings</Title>
          {summary?.last_checked && (
            <Text type="secondary">
              Last checked: {new Date(summary.last_checked).toLocaleString()}
            </Text>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleCheckNow}
            loading={checking}
          >
            Check Now
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Keywords"
                  value={summary?.total_keywords || 0}
                  prefix={<SearchOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Ranked"
                  value={summary?.ranked_keywords || 0}
                  suffix={`/ ${summary?.total_keywords || 0}`}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Progress
                  percent={summary?.total_keywords ? Math.round((summary.ranked_keywords / summary.total_keywords) * 100) : 0}
                  strokeColor="#52c41a"
                  showInfo={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Best Position"
                  value={summary?.best_position || '-'}
                  prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: '#faad14' }}
                />
                <Text type="secondary">{summary?.best_keyword || ''}</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Position"
                  value={summary?.avg_position ? summary.avg_position.toFixed(1) : '-'}
                />
              </Card>
            </Col>
          </Row>

          {/* Alert if no rankings */}
          {summary?.ranked_keywords === 0 && (
            <Alert
              type="info"
              message="No Rankings Yet"
              description="Your site isn't ranking for any tracked keywords yet. This is normal for new sites. Focus on creating quality content and building backlinks."
              style={{ marginBottom: 24 }}
              showIcon
            />
          )}

          {/* Tabs */}
          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab="Current Rankings" key="rankings">
                <Table
                  columns={rankingsColumns}
                  dataSource={rankings}
                  rowKey="keyword"
                  pagination={{ pageSize: 20 }}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => fetchKeywordHistory(record.keyword),
                    style: { cursor: 'pointer' }
                  })}
                />
              </TabPane>

              <TabPane tab="Trends" key="trends">
                <Table
                  columns={trendsColumns}
                  dataSource={trends}
                  rowKey="keyword"
                  pagination={{ pageSize: 20 }}
                  size="small"
                />
              </TabPane>

              <TabPane tab="History" key="history">
                {selectedKeyword ? (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <Title level={4} style={{ margin: 0 }}>Ranking History: "{selectedKeyword}"</Title>
                      <Button onClick={() => setActiveTab('rankings')}>← Back to Rankings</Button>
                    </div>
                    {historyData.length > 0 ? (
                      <Line {...historyChartConfig} />
                    ) : (
                      <Empty description="No history data available for this keyword yet" />
                    )}
                  </div>
                ) : (
                  <Empty description="Click on any keyword row in the Rankings tab to view its history" />
                )}
              </TabPane>

              <TabPane tab="Manage Keywords" key="manage">
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddModalVisible(true)}
                  >
                    Add Keyword
                  </Button>
                </div>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={16}>
                    <Table
                      columns={keywordsColumns}
                      dataSource={keywords}
                      rowKey="id"
                      pagination={{ pageSize: 15 }}
                      size="small"
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <Card title="Keywords by Category" size="small">
                      {categoryData.length > 0 ? (
                        <Column {...categoryChartConfig} />
                      ) : (
                        <Empty description="No keywords" />
                      )}
                    </Card>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}

      {/* Add Keyword Modal */}
      <Modal
        title="Add Keyword to Track"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddKeyword}>
          <Form.Item
            name="keyword"
            label="Keyword"
            rules={[{ required: true, message: 'Please enter a keyword' }]}
          >
            <Input placeholder="e.g., ai stock picker" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            initialValue="general"
          >
            <Select>
              <Select.Option value="high_intent">High Intent</Select.Option>
              <Select.Option value="product">Product</Select.Option>
              <Select.Option value="comparison">Comparison</Select.Option>
              <Select.Option value="strategy">Strategy</Select.Option>
              <Select.Option value="long_tail">Long Tail</Select.Option>
              <Select.Option value="branded">Branded</Select.Option>
              <Select.Option value="general">General</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Add Keyword
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SEORankings
